import { useState, useCallback, useRef, useEffect } from "react";
import { RelayPool } from "applesauce-relay";
import { History, ChevronUp, ExternalLink, Loader2, ArrowRight } from "lucide-react";
import { useNsiteSnapshots } from "@/hooks/useNsiteSnapshots";
import { buildSnapshotUrl, DEFAULT_NSITE_GATEWAY } from "@/lib/nsite";

/** Format a unix timestamp as a short human date. */
function formatDate(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Format a unix timestamp with time. */
function formatDateTime(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Extract a tag value from a nostr event's tags. */
function getTagValue(tags: string[][], name: string): string | undefined {
  return tags.find(([t]) => t === name)?.[1];
}

interface SnapshotVersionsProps {
  pool: RelayPool;
}

export function SnapshotVersions({ pool }: SnapshotVersionsProps) {
  const { snapshots, context, currentSnapshotId, latestUrl, loading } =
    useNsiteSnapshots(pool);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const isViewingSnapshot = currentSnapshotId !== null;

  // Find the currently-viewed snapshot event (if any) for display.
  const currentSnapshot = isViewingSnapshot
    ? snapshots.find((s) => s.id === currentSnapshotId)
    : null;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const navigateToSnapshot = useCallback(
    (snapshotId: string) => {
      const gateway = context.gateway ?? DEFAULT_NSITE_GATEWAY;
      const protocol = window.location.protocol;
      const url = buildSnapshotUrl(snapshotId, gateway, protocol);
      window.location.href = url;
    },
    [context],
  );

  // Don't show the component if there's nothing to show:
  //  - Still loading with no results yet and not viewing a snapshot
  //  - No snapshots found and not viewing a snapshot
  if (!isViewingSnapshot && !loading && snapshots.length === 0) return null;

  // Build the button label.
  const buttonLabel = isViewingSnapshot
    ? currentSnapshot
      ? `Snapshot · ${formatDate(currentSnapshot.created_at)}`
      : "Snapshot"
    : "Older versions";

  return (
    <div ref={panelRef} className="absolute bottom-4 left-4 z-20 pointer-events-auto">
      {/* Toggle button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-black/40 border border-white/20 text-white/70 hover:text-white hover:bg-black/60 rounded-md backdrop-blur transition-colors select-none"
        title="View site versions"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <History className="h-3.5 w-3.5" />
        )}
        <span>{buttonLabel}</span>
        {!loading && snapshots.length > 0 && (
          <span className="ml-0.5 text-white/40">({snapshots.length})</span>
        )}
        <ChevronUp
          className={`h-3 w-3 transition-transform ${open ? "" : "rotate-180"}`}
        />
      </button>

      {/* Expanded panel */}
      {open && (
        <div className="absolute bottom-full mb-2 left-0 w-80 max-h-80 flex flex-col rounded-lg bg-slate-900/95 backdrop-blur border border-white/10 shadow-xl">
          {/* Header */}
          <div className="px-3 py-2 border-b border-white/10 shrink-0">
            <p className="text-xs text-white/50 uppercase tracking-wider font-medium">
              Site Versions
            </p>
          </div>

          {/* Latest version link */}
          {latestUrl && (
            <a
              href={latestUrl}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-amber-300/90 hover:text-amber-200 hover:bg-white/5 transition-colors border-b border-white/10 shrink-0"
            >
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              <span className="font-medium">Latest version</span>
              {!isViewingSnapshot && (
                <span className="ml-auto text-xs text-white/30">(current)</span>
              )}
            </a>
          )}

          {/* Snapshot list */}
          <div className="overflow-y-auto flex-1">
            {loading && snapshots.length === 0 ? (
              <div className="flex items-center justify-center py-6 gap-2 text-white/50 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : snapshots.length === 0 ? (
              <div className="flex items-center justify-center py-6 text-white/40 text-sm">
                No snapshots found
              </div>
            ) : (
              <div className="flex flex-col">
                {snapshots.map((snap) => {
                  const title = getTagValue(snap.tags, "title");
                  const desc = getTagValue(snap.tags, "description");
                  const xTag = snap.tags.find(
                    ([t, , marker]) => t === "x" && marker === "aggregate",
                  );
                  const aggregateHash = xTag?.[1];
                  const isCurrent = snap.id === currentSnapshotId;

                  return (
                    <button
                      key={snap.id}
                      onClick={() => navigateToSnapshot(snap.id)}
                      className={`flex items-start gap-3 px-3 py-2.5 text-left transition-colors group border-b border-white/5 last:border-b-0 ${
                        isCurrent
                          ? "bg-amber-500/10 border-l-2 border-l-amber-400/60"
                          : "hover:bg-white/5"
                      }`}
                      title={`Open snapshot from ${formatDateTime(snap.created_at)}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm truncate ${
                              isCurrent ? "text-amber-200/90" : "text-white/90"
                            }`}
                          >
                            {title || formatDate(snap.created_at)}
                          </span>
                          {title && (
                            <span className="text-xs text-white/40 shrink-0">
                              {formatDate(snap.created_at)}
                            </span>
                          )}
                          {isCurrent && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300/90 font-medium shrink-0">
                              viewing
                            </span>
                          )}
                        </div>
                        {desc && (
                          <p className="text-xs text-white/40 truncate mt-0.5">
                            {desc}
                          </p>
                        )}
                        {aggregateHash && (
                          <p className="text-xs font-mono text-white/25 truncate mt-0.5">
                            {aggregateHash.slice(0, 12)}…
                          </p>
                        )}
                      </div>
                      {!isCurrent && (
                        <ExternalLink className="h-3.5 w-3.5 text-white/20 group-hover:text-white/50 shrink-0 mt-0.5 transition-colors" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
